/**
 * The detail panel below 1100 px: a sheet that slides over the bottom of the
 * stage. The handle is a focusable splitter (the window-splitter pattern):
 * drag it, click it to cycle peek, half and full, or use the arrow keys, Home
 * and End. A drag that ends well below the peek closes the sheet, as does the
 * close button; the top bar's detail-panel button opens it again.
 */
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { PanelBottomClose } from 'lucide-react';
import type { Mode } from '@/state/store';
import { IconButton } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { DetailPanel } from './DetailPanel';
import { SHEET_FRACTION, sheetHeightPx, snapSheet, stepSheet, type SheetSnap } from './layout';

const SNAP_LABEL: Record<SheetSnap, string> = { peek: 'Peek', half: 'Half', full: 'Full' };

export function DetailSheet({
  mode,
  snap,
  containerHeight,
  onSnap,
  onClose,
  onDragHeight,
}: {
  mode: Mode;
  snap: SheetSnap;
  containerHeight: number;
  onSnap: (snap: SheetSnap) => void;
  onClose: () => void;
  /** The live height while a drag is in progress, null when it ends (the stage lifts its HUD by it). */
  onDragHeight: (px: number | null) => void;
}) {
  const [dragPx, setDragPx] = useState<number | null>(null);
  const [entered, setEntered] = useState(false);
  const drag = useRef<{ startY: number; startHeight: number; moved: boolean } | null>(null);
  const snapped = sheetHeightPx(snap, containerHeight);
  const height = dragPx ?? snapped;

  // Slide in from below on open (the reduced-motion rule makes it instant).
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const setLive = (px: number | null) => {
    setDragPx(px);
    onDragHeight(px);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    drag.current = { startY: e.clientY, startHeight: height, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const dy = d.startY - e.clientY;
    if (!d.moved && Math.abs(dy) < 4) return;
    d.moved = true;
    setLive(Math.max(0, Math.min(containerHeight, d.startHeight + dy)));
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (!d.moved) {
      onSnap(stepSheet(snap, 'cycle'));
      return;
    }
    const ended = Math.max(0, Math.min(containerHeight, d.startHeight + (d.startY - e.clientY)));
    setLive(null);
    const result = snapSheet(ended, containerHeight);
    if (result === 'closed') onClose();
    else onSnap(result);
  };
  const onPointerCancel = () => {
    drag.current = null;
    setLive(null);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    let next: SheetSnap | null = null;
    if (e.key === 'ArrowUp') next = stepSheet(snap, 'up');
    else if (e.key === 'ArrowDown') next = stepSheet(snap, 'down');
    else if (e.key === 'Home') next = 'peek';
    else if (e.key === 'End') next = 'full';
    else if (e.key === 'Enter' || e.key === ' ') next = stepSheet(snap, 'cycle');
    if (!next) return;
    e.preventDefault();
    onSnap(next);
  };

  const percent = Math.round(SHEET_FRACTION[snap] * 100);
  return (
    <aside
      id="detail-panel"
      aria-label="Detail panel"
      data-testid="detail-panel"
      data-presentation="sheet"
      data-snap={snap}
      className={cn(
        'absolute inset-x-0 bottom-0 z-40 flex flex-col overflow-hidden rounded-t-[14px] border-t border-hairline-strong bg-raised shadow-[0_-8px_24px_rgb(0_0_0/0.45)]',
        dragPx === null && 'transition-[height,transform] duration-200 ease-out',
        entered ? 'translate-y-0' : 'translate-y-full',
      )}
      style={{ height }}
    >
      <div className="relative flex h-6 shrink-0 items-center justify-center">
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize the detail panel"
          aria-controls="detail-panel"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-valuetext={`${SNAP_LABEL[snap]}, ${percent}% of the stage`}
          tabIndex={0}
          title="Drag, click or use the arrow keys to resize"
          data-testid="sheet-handle"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onKeyDown={onKeyDown}
          className="flex h-6 w-24 cursor-row-resize touch-none items-center justify-center rounded-full"
        >
          <span aria-hidden className="h-1 w-10 rounded-full bg-ink-3" />
        </div>
        {/* The panel icon (as on the top bar), so it does not read as the header's clear-selection X below it. */}
        <IconButton label="Close the detail panel" onClick={onClose} className="absolute right-1.5 top-0.5 h-7 w-7">
          <PanelBottomClose className="h-4 w-4" />
        </IconButton>
      </div>
      <div className="min-h-0 flex-1">
        <DetailPanel mode={mode} presentation="sheet" />
      </div>
    </aside>
  );
}
